import { forwardRef } from "react";

import {
  TablePaginationProps,
  Pagination as MUIPagination,
  Box,
} from "@mui/material";
import {
  DataGridPro,
  GridPagination,
  gridPageCountSelector,
  gridFilteredTopLevelRowCountSelector,
  gridPageSizeSelector,
  useGridApiContext,
  useGridSelector,
  useGridRootProps,
} from "@mui/x-data-grid-pro";

import { DataGridProps, DataGridReference } from "./DataGrid.types";

function Pagination({
  page,
  className,
  onPageChange,
}: Pick<TablePaginationProps, "page" | "onPageChange" | "className">) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const visibleTopLevelRowCount = useGridSelector(
    apiRef,
    gridFilteredTopLevelRowCountSelector
  );
  const isClientMode = rootProps.paginationMode === "client";

  return (
    <MUIPagination
      showFirstButton
      showLastButton
      color="primary"
      shape="rounded"
      variant="outlined"
      className={className}
      count={
        isClientMode
          ? pageCount
          : Math.ceil(
              (rootProps.rowCount ?? visibleTopLevelRowCount) / pageSize
            )
      }
      page={page + 1}
      onChange={(event, newPage) => {
        onPageChange(
          event as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>,
          newPage - 1
        );
      }}
    />
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomPagination(props: any) {
  return <GridPagination ActionsComponent={Pagination} {...props} />;
}

const DataGrid = forwardRef<DataGridReference, DataGridProps>(
  ({
    containerProps,
    containerHeight,
    columnHeaderHeight = 44,
    getRowHeight = () => "auto",
    disableRowSelectionOnClick = true,
    pageSizeOptions = [5, 10, 25, 50, 100],
    autoHeight: defaultAutoHeight,
    ...innerProps
  }) => {
    return (
      <Box
        height={defaultAutoHeight ? undefined : containerHeight}
        {...containerProps}
      >
        <DataGridPro
          {...innerProps}
          columnHeaderHeight={columnHeaderHeight}
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          pageSizeOptions={pageSizeOptions}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            ...innerProps.initialState,
          }}
          getRowHeight={getRowHeight}
          slots={{
            pagination: CustomPagination,
            ...innerProps.slots,
          }}
        />
      </Box>
    );
  }
);

export default DataGrid;
